import { LoginInfo, ApiInterface, Interceptor, InterceptorLoginConfig, WebLoginMode, CacheType, LoginType } from '../types';
import {
  getCookie,
  sleep,
  TENVIDEO_GET_USERINFO_API,
  getLoginInfo,
  report,
  handleSuccess,
  handleError,
  getTenvideoLoginMiddlePage,
  time33,
  envHelper,
  cache,
  TENVIDEO_APP_LOGIN_SOURCE,
} from '../helper';
import JsApi from '@tencent/tvf-jsapi';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Base } from './base';

// @ts-ignore
const version = __IWAN_LOGIN_VERSION__ || 'unknown';
const logPrefix = `[iwan-login/tenvideo@${version}]`;

const clearTenvideoCookies = () => {
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  cookies.forEach((cookie) => {
    const [key] = cookie.split('=');
    Cookies.remove(key, { domain: 'iwan.qq.com' });
  });
};

/**
 * 腾讯视频
 */
export class TenvideoApi extends Base implements ApiInterface {
  /**
   * 静态属性是否在App内，处理外部可能把实例方法当作普通方法调用的问题
   */
  public static staticIsInApp: boolean;

  public needRefresh = false; // 是否需要续期，外部可以根据此属性进行事件监听并在续期后处理逻辑
  public refreshed = false;
  public ifRefreshSuccess = false;
  public isHidePhoneLogin = true;

  /**
   * 是否在客户端内
   */
  private isInApp: boolean;

  public constructor({ isInApp }: { isInApp: boolean }) {
    super();
    this.isInApp = isInApp;
  }

  /**
   * App 内禁用头像/昵称缓存，防止修改头像/昵称不生效
   */
  get isDisableCache() {
    const isDisable = this.isInApp;
    console.log(logPrefix, 'isDisableCache', isDisable);
    return this.isInApp;
  }

  /**
   * 登录
   */
  @report('login')
  public async login() {
    // 端内
    if (this.isInApp) {
      try {
        let errCode;
        let errMsg;

        console.log(logPrefix, 'login isHidePhoneLogin', this.isHidePhoneLogin);

        // 当屏蔽手机登录时，在终端支持屏蔽情况下，调用新接口，否则调用老接口
        if (this.isHidePhoneLogin) {
          const isSupportPhoneLogin = await this.isSupportPhoneLogin();
          if (isSupportPhoneLogin) {
            ({ errCode, errMsg } = await JsApi.invoke('showLoginDialog', {
              type: 'tv',
              loginSource: TENVIDEO_APP_LOGIN_SOURCE,
              tvfOptions: {
                timeout: 0,
              },
            }));
          } else {
            ({ errCode, errMsg } = await JsApi.invoke('actionLogin', { type: 'tv', loginSource: TENVIDEO_APP_LOGIN_SOURCE }));
          }
        } else {
          ({ errCode, errMsg } = await JsApi.invoke('actionLogin', { type: 'tv', loginSource: TENVIDEO_APP_LOGIN_SOURCE }));
        }

        // 视频IOS端内种cookie有延迟，等待500ms返回
        if (envHelper.isIos) {
          await sleep(500);
        }

        if (errCode !== 0) {
          return handleError(errCode, errMsg);
        }
        return handleSuccess(true);
      } catch (error) {
        console.error(logPrefix, 'login error', error);
        const { errCode } = error as any;
        if (errCode === 3003) {
          return handleError(-1, '当前版本过低，可能会导致登录失败，请升级至最新版本再试~');
        }
        // 如果取消登录会报错
        return handleError(-1, '取消登录');
      }
    }

    // 端外执行拦截器
    const chain: Interceptor<InterceptorLoginConfig>[] = [];
    let promise = Promise.resolve<InterceptorLoginConfig>({
      webLoginMode: undefined,
    });
    this.interceptors.login.forEach((interceptor) => {
      chain.push(interceptor);
    });
    while (chain.length > 0) {
      const { resolved, rejected } = chain.shift()!;
      promise = promise.then(resolved, rejected);
    }
    const { webLoginMode } = await promise;
    if (webLoginMode === WebLoginMode.CANCEL) {
      return handleError(-1, '取消登录');
    }

    // 端外跳中间页
    window.location.href = getTenvideoLoginMiddlePage(webLoginMode);

    // 如果业务侧代码有 location.reload()，那么等待两秒返回以保证 href 正确跳转
    await sleep(2000);

    // IOS 和部分安卓在 location.href 跳转页面后，此时如果没有登录而是直接回退页面时不会刷新页面
    // 因此状态码返回 -1 为了防止 JS 继续执行时误判登录成功
    return handleError(-1, '取消登录');
  }

  /**
   * 退出登录
   *
   * @param needReload 是否需要刷新页面
   */
  @report('logout')
  public async logout(needReload = true) {
    // 未登录不允许调用
    const { result: isLogin } = await this.isLogin();

    // 端内
    if (this.isInApp) {
      // 1. 当屏蔽手机登录时，会把手机登录当做未登录，因此还需排除掉手机登录的情况
      //    只有真正未登录时，才不允许退出
      // 2. 当不屏蔽手机登录时，会返回实际登录情况，可以直接用来判断
      if (this.isHidePhoneLogin) {
        const isPhoneLogin = await this.isPhoneLogin();
        // 真正的未登录
        if (!isLogin && !isPhoneLogin) {
          return handleError(-1, '当前未登录，无法退出登录');
        }
      } else if (!isLogin) {
        return handleError(-1, '当前未登录，无法退出登录');
      }

      const { errCode, errMsg } = await JsApi.invoke('logout');
      if (errCode !== 0) {
        return handleError(errCode, errMsg);
      }

      return handleSuccess(true);
    }

    // 端外清空 cookie
    clearTenvideoCookies();
    if (needReload) {
      location.reload();
    }
    return handleSuccess(true);
  }

  /**
   * 是否登录
   *
   * QQ和微信登录: 都有 openid 和 access_token
   * 未登录: 没有 openid 和 access_token
   * 手机登录，只有 openid，要根据是否隐藏手机登录来判断是否当做未登录处理
   */
  @report('isLogin')
  public async isLogin() {
    const openid = getCookie('vqq_openid') || getCookie('openid');
    const accessToken = getCookie('vqq_access_token') || getCookie('access_token');

    // 在端内，因为手机登录只有 openid，所以:
    // 1. 当屏蔽手机登录时，如果是手机登录，直接返回未登录
    // 2. 当不屏蔽手机登录时，如果是手机登录，判断 openid
    // 其他情况，需要同时判断 openid 和 access_token
    if (TenvideoApi.staticIsInApp) {
      const isPhoneLogin = await this.isPhoneLogin();

      if (this.isHidePhoneLogin) {
        if (isPhoneLogin) {
          return handleSuccess(false);
        }
      } else if (isPhoneLogin) {
        return handleSuccess(!!openid);
      }
    }

    // openid 和 accessToken 同时存在时认为已登录
    // 因此如果登录了，但是已过期，则当做未登录
    return handleSuccess(!!(openid && accessToken));
  }

  /**
   * 获取登录信息，cookie
   */
  @report('getLoginInfo')
  public async getLoginInfo() {
    // 当屏蔽手机登录并且是手机登录时，当做未登录处理，返回空的信息
    if (this.isHidePhoneLogin) {
      const isPhoneLogin = await this.isPhoneLogin();
      if (isPhoneLogin) {
        return handleSuccess({
          type: LoginType.nologin,
          appid: '',
          openid: '',
          accessToken: '',
          refreshToken: '',
          vuserid: '',
          vusession: '',
        });
      }
    }

    return getLoginInfo({
      isInApp: this.isInApp,
      loginInfoHandler: (_loginInfo: LoginInfo, appCookies: Record<string, string>) => {
        const loginInfo = _loginInfo;

        loginInfo.appid = getCookie('vqq_appid', appCookies) || getCookie('appid', appCookies);
        loginInfo.openid = getCookie('vqq_openid', appCookies) || getCookie('openid', appCookies);
        loginInfo.accessToken = getCookie('vqq_access_token', appCookies) || getCookie('access_token', appCookies);
        loginInfo.refreshToken = getCookie('vqq_refresh_token', appCookies) || getCookie('refresh_token', appCookies);
        loginInfo.vuserid = getCookie('vuserid', appCookies) || getCookie('vqq_vuserid', appCookies);
        loginInfo.vusession = getCookie('vusession', appCookies) || getCookie('vqq_vusession', appCookies);
      },
    });
  }

  /**
   * 获取用户信息，头像昵称
   */
  @report('getUserInfo')
  @cache(CacheType.USER_INFO)
  public async getUserInfo() {
    const userInfo = {
      nickname: '',
      avatar: '',
    };

    // 端内
    if (this.isInApp) {
      try {
        const { errCode, errMsg, result } = await JsApi.invoke('getMainUserInfo');
        if (errCode !== 0) {
          return handleError(errCode, errMsg);
        }

        const isPhoneLogin = result?.type === LoginType.phone;

        // 当屏蔽手机登录时，并且是手机登录时，返回空的用户信息
        if (this.isHidePhoneLogin && isPhoneLogin) {
          userInfo.nickname = '';
          userInfo.avatar = '';
        } else {
          userInfo.nickname = result?.userInfo?.nickname || '';
          userInfo.avatar = result?.userInfo?.headImgUrl || '';
        }
      } catch (error) {
        console.error(logPrefix, 'getUserInfo error', error);
        return handleError(-1, '未登录，无法获取用户信息');
      }
    } else {
      const { result: isLogin } = await this.isLogin();
      if (!isLogin) {
        return handleError(-1, '未登录，无法获取用户信息');
      }

      const res = await axios.get(TENVIDEO_GET_USERINFO_API, {
        params: {
          token: time33((getCookie('vqq_access_token') || getCookie('access_token')).substring(0, 10)),
          acctype: getCookie('main_login'),
        },
        withCredentials: true,
      });
      const { code, msg, data } = res.data;
      if (code !== '100000') {
        console.error(logPrefix, 'getUserInfo error', code, msg);
        return handleError(-1, msg);
      }
      userInfo.nickname = data.nick;
      userInfo.avatar = data.avatar;
    }

    console.log(logPrefix, 'getUserInfo success', userInfo);
    return handleSuccess(userInfo);
  }

  /**
   * 是否手机登录
   */
  private async isPhoneLogin() {
    if (this.isInApp) {
      const { result } = await JsApi.invoke('getMainCookie');
      return result.type === LoginType.phone;
    }
    return getCookie('main_login') === LoginType.phone;
  }

  /**
   * 是否支持手机登录
   */
  private async isSupportPhoneLogin() {
    if (this.isInApp) {
      const { result } = await JsApi.invoke('checkApi', {
        apiList: ['showLoginDialog'],
      });
      return result?.showLoginDialog;
    }
    // 端外一律认为不支持
    return false;
  }
}
